import { memo } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';

interface FilterHeaderProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onSearchSubmit: (value: string) => void;
    selectedGroupIds: number[];
    onSelectedGroupIdsChange: (ids: number[]) => void;
    availableGroups: Array<{ id: number; description: string }>;
    loadingGroups: boolean;
    isFromCompany: 0 | 1 | undefined;
    onTypeChange: (value: 0 | 1 | undefined) => void;
    onReset: () => void;
    totalItems: number;
}

export const FilterHeader = memo<FilterHeaderProps>(({
    searchTerm,
    onSearchChange,
    onSearchSubmit,
    selectedGroupIds,
    onSelectedGroupIdsChange,
    availableGroups,
    loadingGroups,
    isFromCompany,
    onTypeChange,
    onReset,
}) => {
    const handleTypeChange = (_: React.MouseEvent<HTMLElement>, newValue: 0 | 1 | null) => {
        onTypeChange(newValue === null ? undefined : newValue);
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            onSearchSubmit(searchTerm);
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel>Groupes</InputLabel>
                        <Select
                            multiple
                            value={selectedGroupIds}
                            onChange={(e) => onSelectedGroupIdsChange(e.target.value as number[])}
                            input={<OutlinedInput label="Groupes" />}
                            disabled={loadingGroups}
                            renderValue={(selected) => {
                                if (selected.length === 0) return <em>Tous les groupes</em>;
                                return `${selected.length} groupe${selected.length > 1 ? 's' : ''} sélectionné${selected.length > 1 ? 's' : ''}`;
                            }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                        >
                            {availableGroups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    <Checkbox checked={selectedGroupIds.includes(group.id)} />
                                    <ListItemText primary={group.description} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedGroupIds.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {selectedGroupIds.map((groupId) => {
                                const group = availableGroups.find(g => g.id === groupId);
                                return group ? (
                                    <Chip
                                        key={groupId}
                                        label={group.description}
                                        onDelete={() => onSelectedGroupIdsChange(selectedGroupIds.filter(id => id !== groupId))}
                                        color="primary"
                                        size="small"
                                    />
                                ) : null;
                            })}
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                        Filtrer par type :
                    </Typography>
                    <ToggleButtonGroup
                        value={isFromCompany}
                        exclusive
                        onChange={handleTypeChange}
                        size="small"
                    >
                        <ToggleButton value={1} sx={{ textTransform: 'none', px: 2 }}>Enseigne</ToggleButton>
                        <ToggleButton value={0} sx={{ textTransform: 'none', px: 2 }}>Personnel</ToggleButton>
                    </ToggleButtonGroup>
                    {isFromCompany !== undefined && (
                        <Chip
                            label={isFromCompany === 1 ? 'Enseigne' : 'Personnel'}
                            onDelete={() => onTypeChange(undefined)}
                            color="primary"
                            size="small"
                        />
                    )}
                </Box>

                <Button size="small" startIcon={<RefreshIcon />} onClick={onReset} sx={{ textTransform: 'none' }}>
                    Réinitialiser
                </Button>
            </Box>
        </Paper>
    );
});

FilterHeader.displayName = 'FilterHeader';
